import { Grid, TextField } from "@material-ui/core";
import { IInstallment } from "api/interfaces";
import MaskedField from "components/UI/Fields/MaskedField";
import { useEffect, useState } from "react";
import useValidations from "../../hooks/useValidations";
import { decimal } from "../../utils/DecimalPrecision";
import DialogModal from "../UI/DialogModals";
import DatePickerField from "../UI/Fields/DatePickerField";
import InstallmentsFields from "../shared/Installments/InstallmentsFields";

import { calcInstallmentTotal } from "../../api/models/InstallmentsModel";
import { lang } from "../../i18nLang";
import { AdvanceStore as Store } from "../../store";

const defaultData: any = {
  title: "",
  total: undefined,
  date: new Date(),
  quantity: 1,
  installments: new Array<IInstallment>(),
};

interface IProps {
  /** Propriedade que indica se o modal está aberto ou fechado */
  open: boolean;
  /** Função que será chamada ao fechar o modal */
  onClose: () => void;
}
/** Modal de cadastro de Adiantamento
 * @category Components **/
const AdvancesForm = ({ open, onClose }: IProps) => {
  const { validated, validateFields, reset, hasErrors } = useValidations(
    Store.validations
  );
  const [editMode, setEditMode] = useState(false);
  const [data, setData] = useState(defaultData);
  const [timeoutId, setTimeoutId] = useState<any>(undefined);

  useEffect(() => {
    const editAdvance = Boolean(Store.currentAdvance);
    const advance = Store.currentAdvance || defaultData;
    reset();

    setEditMode(editAdvance);
    setData({
      title: advance.title,
      total: advance.total,
      date: advance.date || new Date(),
      quantity: advance.quantity,
      installments: advance.installments,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChangeInput = (ev) => {
    const { name, value } = ev.target;
    if (name && value !== undefined) {
      return changeData(name, value);
    }
  };

  const handleChangeQuantity = (ev) => {
    const newData = handleChangeInput(ev) || data;
    clearTimeout(timeoutId);

    const newTimeoutId = setTimeout(() => {
      createInstallments(newData);
    }, 2500);
    setTimeoutId(newTimeoutId);
  };

  const changeData = (name, value) => {
    const newData = { ...data };
    newData[name] = value;

    if (name === "quantity" && value) {
      newData.quantity = Math.round(value);
    }

    if (name === "installments" && value.length !== newData.quantity) {
      newData.quantity = value.length || 1;
    }

    setData(newData);
    return newData;
  };

  const handleClickConfirm = () => {
    if (!validateFields(data, true)) return true;
    if (editMode) Store.updateAdvance({ ...data });
    else Store.createAdvance({ ...data });
    return;
  };

  const handleBlurInput = (name: string, value) => {
    validateFields({ [name]: value });
    createInstallments(data);
  };

  const handleKeyDownInput = (ev) => {
    if (ev.key === "Enter") createInstallments(data);
  };

  const createInstallments = (newData) => {
    let newArray = Store.createInstallments(newData, editMode);
    handleChangeInstallments(newArray);
  };

  const handleChangeInstallments = (newInstallments: IInstallment[]) => {
    changeData("installments", newInstallments);
  };

  const handleChangeDate = (newDate: Date) => {
    changeData("date", newDate);
  };

  const installmentsTotal = calcInstallmentTotal(data.installments);
  const hasTotalDiff = installmentsTotal !== decimal(data.total, 2).round();
  const hasError = hasTotalDiff || hasErrors();

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title={lang(`advances.${editMode ? "formTitleOnEditMode" : "formTitle"}`)}
      confirmLabel={lang(editMode ? "save" : "add")}
      disabledConfirm={hasError}
      onConfirm={handleClickConfirm}
    >
      <TextField
        name="title"
        label={lang("fields.title")}
        fullWidth
        margin="normal"
        value={data.title}
        onChange={handleChangeInput}
        onBlur={(ev) => validateFields({ title: ev.target?.value })}
        error={!validated.title.valid}
        helperText={validated.title.text}
      />
      <Grid container spacing={4}>
        <Grid item xs>
          <MaskedField
            name="total"
            label={lang("fields.value")}
            fullWidth
            margin="normal"
            prefix={`${lang("currency")} `}
            fixedDecimalScale
            allowNegative={false}
            align={"right"}
            value={data.total}
            onChange={handleChangeInput}
            onBlur={(ev, value) => handleBlurInput("total", value)}
            onKeyDown={handleKeyDownInput}
            error={!validated.total.valid}
            helperText={validated.total.text}
          />
        </Grid>
        <Grid item xs>
          <DatePickerField
            name="date"
            label={lang("fields.date")}
            margin="normal"
            value={data.date}
            onChange={handleChangeDate}
            onBlur={(name, newDate) => validateFields({ [name]: newDate })}
            error={!validated.date.valid}
            helperText={validated.date.text}
          />
        </Grid>
        <Grid item xs>
          <TextField
            name="quantity"
            label={lang("advances.fields.quantity")}
            type="number"
            fullWidth
            margin="normal"
            value={data.quantity}
            onChange={handleChangeQuantity}
            onBlur={(ev) => handleBlurInput("quantity", ev.target.value)}
            onKeyDown={handleKeyDownInput}
            error={!validated.quantity.valid}
            helperText={validated.quantity.text}
          />
        </Grid>
      </Grid>
      <InstallmentsFields
        installments={data.installments}
        onChange={handleChangeInstallments}
        total={installmentsTotal}
        hasTotalDiff={hasTotalDiff}
        diffErrorLabel={lang("advances.error.totalDiff")}
      />
    </DialogModal>
  );
};

export default AdvancesForm;
